import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, apiError, toInputDateUK } from "@/lib/api";
import { setUnsaved, confirmLeave } from "@/lib/unsaved";
import DocumentUpload from "@/components/DocumentUpload";
import ConfirmDetails from "@/components/ConfirmDetails";
import ZAmounts from "@/components/ZAmounts";
import { todayUK } from "@/components/PaymentChoice";
import { emptyZ, fromZ, zPayload, computedTotal, cardTotal, totalMismatch, layoutFor, refundTotal, refundSplitMismatch } from "@/lib/z";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save, Send, AlertTriangle, Paperclip } from "lucide-react";

const Field = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-sm font-medium text-navy">{label}</Label>{children}</div>);

function DuplicateNotice({ dup, onOpen }) {
  if (!dup) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2" data-testid="z-duplicate-notice">
      <p className="text-sm text-red-800 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{dup.message}</p>
      {dup.existing_id && <button type="button" onClick={onOpen} className="text-sm underline text-red-800 min-h-[44px]" data-testid="z-open-existing-btn">Open the existing Z report</button>}
    </div>
  );
}

function Attach({ show, setShow, form, setDocs }) {
  if (!show) {
    return <button type="button" onClick={() => setShow(true)} className="text-sm text-navy/60 underline min-h-[44px]" data-testid="z-attach-toggle"><Paperclip className="inline w-4 h-4 mr-1" />Attach a photo of the Z report (optional)</button>;
  }
  return (
    <div className="space-y-1" data-testid="z-attach-section">
      <p className="text-xs text-navy/50">Optional. The photo is stored with the record only — the figures are not read from it, so type them in on the next step.</p>
      <DocumentUpload kind="z_report" storeId={parseInt(form.store_id)} onUpload={(d) => setDocs((p) => [...p, d])} onRemove={(did) => setDocs((p) => p.filter((x) => x.id !== did))} />
    </div>
  );
}

export default function RecordZPage() {
  const { id } = useParams();
  const { activeStoreId, stores } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyZ(activeStoreId || stores[0]?.id, todayUK()));
  const [docs, setDocs] = useState([]);
  const [ai] = useState(null);
  const [attach, setAttach] = useState(false);
  const [step, setStep] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const [loaded, setLoaded] = useState(!id);
  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (["trading_date", "register_ref", "store_id"].includes(k)) setDup(null); };

  useEffect(() => {
    if (!id) { return; }
    api.get(`/z-closings/${id}`).then((r) => { setForm(fromZ(r.data, toInputDateUK)); setDocs(r.data.documents || []); setAttach((r.data.documents || []).length > 0); setLoaded(true); }).catch(() => toast.error("Z report not found"));
  }, [id]);
  useEffect(() => { document.querySelector("[data-testid=main-scroll]")?.scrollTo(0, 0); }, [step]);
  useEffect(() => { setUnsaved(true); return () => setUnsaved(false); }, []);

  const save = async (submit) => {
    if (!form.store_id) return toast.error("Select a store first");
    setSaving(true);
    try {
      const body = zPayload({ ...form, source: docs.length ? "upload" : "manual" }, docs, ai, submit, confirmed);
      const r = id ? await api.put(`/z-closings/${id}`, body) : await api.post("/z-closings", body);
      toast.success(submit ? "Z report submitted for manager review" : "Draft saved");
      setUnsaved(false);
      navigate(`/z-closings/${r.data.id}`, { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "duplicate_z_report") { setDup(d); setStep(1); } else toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const canNext = form.store_id && form.trading_date && form.register_ref.trim();
  if (!loaded) return <p className="text-sm text-navy/50" data-testid="z-edit-loading">Loading Z report…</p>;
  return (
    <div className="space-y-5" data-testid="record-z-page">
      <div className="flex items-center gap-3">
        <button onClick={() => { if (confirmLeave()) navigate(-1); }} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back" data-testid="z-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">{id ? "Correct Z Report" : "Daily Z Closing"}</h1>
        <span className="ml-auto text-xs text-navy/40" data-testid="z-step-label">Step {step} of 3</span>
      </div>
      <DuplicateNotice dup={dup} onOpen={() => navigate(`/z-closings/${dup.existing_id}`)} />

      {step === 1 && (
        <div className="space-y-4">
          {stores.length > 1 && (
            <Field label="Store">
              <Select value={String(form.store_id)} onValueChange={(v) => set("store_id", v)}>
                <SelectTrigger className="gm-input" data-testid="z-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Trading date"><Input value={form.trading_date} onChange={(e) => set("trading_date", e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="z-date-input" /></Field>
            <Field label="Register / Z number"><Input value={form.register_ref} onChange={(e) => set("register_ref", e.target.value)} className="gm-input" data-testid="z-register-input" /></Field>
          </div>
          <button onClick={() => setStep(2)} disabled={!canNext} className="w-full gm-btn-gold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="z-step1-next-btn">Enter the figures <ArrowRight className="w-4 h-4" /></button>
          <Attach show={attach} setShow={setAttach} form={form} setDocs={setDocs} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <ZAmounts form={form} set={set} />
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="flex-1 gm-btn-navy flex items-center justify-center gap-2" data-testid="z-step2-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => setStep(3)} disabled={totalMismatch(form) || !layoutFor(form) || refundSplitMismatch(form)} className="flex-1 gm-btn-gold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="z-step2-next-btn">Next <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && <Review form={form} docs={docs} set={set} setStep={setStep} confirmed={confirmed} setConfirmed={setConfirmed} save={save} saving={saving} stores={stores} />}
    </div>
  );
}

function Review({ form, docs, set, setStep, confirmed, setConfirmed, save, saving, stores }) {
  const store = stores.find((s) => String(s.id) === String(form.store_id));
  const row = (k, v, t) => (<><span className="text-navy/50">{k}</span><span className="text-navy font-mono text-right" data-testid={t}>{v}</span></>);
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-navy/10 bg-white p-4 grid grid-cols-2 gap-y-1.5 text-sm" data-testid="z-summary">
        <span className="text-navy/50">Store</span><span className="text-navy text-right">{store?.name}</span>
        <span className="text-navy/50">Date · register</span><span className="text-navy text-right">{form.trading_date} · {form.register_ref}</span>
        {row("Cash", formatGBP(form.cash_sales || 0), "z-sum-cash")}
        {row(form.card_mode === "machines" ? `Card (${form.card_machines.length} machines)` : "Card", formatGBP(cardTotal(form)), "z-sum-card")}
        {row("Account / credit", formatGBP(form.account_sales || 0), "z-sum-account")}
        {row("Bank transfer", formatGBP(form.bank_transfer_sales || 0), "z-sum-bank")}
        {row("Other tenders", formatGBP(form.other_tenders.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0)), "z-sum-other")}
        {row("Cash refunds", parseFloat(form.cash_refunds) ? `-${formatGBP(form.cash_refunds)}` : formatGBP(0), "z-sum-cash-refunds")}
        {row("Card / other refunds", formatGBP((parseFloat(form.card_refunds) || 0) + (parseFloat(form.other_refunds) || 0)), "z-sum-other-refunds")}
        {refundTotal(form) > 0 && row("Tender figures", layoutFor(form) === "net" ? "after refunds" : "before refunds", "z-sum-refund-layout")}
        {row("Adjustments", formatGBP(form.adjustments || 0), "z-sum-adjustments")}
        <span className="text-navy font-semibold">Total sales</span><span className="text-navy font-mono font-bold text-right" data-testid="z-sum-total">{formatGBP(computedTotal(form))}</span>
        <span className="text-navy/50">Z report document</span><span className="text-navy text-right">{form.source === "upload" ? `${docs.length} attached` : "Typed in"}</span>
      </div>
      <Field label="Notes (optional)"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="gm-input" data-testid="z-notes-input" /></Field>
      <button onClick={() => setStep(2)} className="w-full gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="z-step3-back-btn"><ArrowLeft className="w-4 h-4" /> Back to amounts</button>
      <div className="flex flex-col gap-2 sticky bottom-0 z-10 bg-cream -mx-4 px-4 pt-3 pb-3 border-t border-navy/10" data-testid="z-sticky-actions">
        <ConfirmDetails checked={confirmed} onChange={setConfirmed} kind="Z report" />
        <div className="flex gap-3">
          <button onClick={() => save(false)} disabled={saving} className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="z-save-draft-btn"><Save className="w-4 h-4" /> Draft</button>
          <button onClick={() => save(true)} disabled={saving || !confirmed} className="flex-1 gm-btn-gold flex items-center justify-center gap-2 text-sm disabled:opacity-40" data-testid="z-submit-btn"><Send className="w-4 h-4" /> Submit</button>
        </div>
      </div>
    </div>
  );
}
